import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState, useMemo } from 'react';
import { inventoryApi } from '@/lib/inventoryApi';
import { toNumber } from '@/lib/number';
import { useCurrentFarm } from '@/hooks/useCurrentFarm';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import {
  Filter,
  ChevronLeft,
  ChevronRight,
  ArrowDownCircle,
  ArrowUpCircle,
  RotateCcw,
  History,
  Minus,
  Trash2,
  ArrowLeftRight,
} from 'lucide-react';
import { TRANSACTION_TYPE_OPTIONS } from '@/types/inventory';
import type { InventoryTransaction } from '@/types/inventory';
import type { ApiListResponse } from '@/types/api';
import { format } from 'date-fns';

export const Route = createFileRoute('/_app/inventory/transactions')({
  component: TransactionsPage,
});

function TransactionsPage() {
  const { farmId, isLoading: isFarmLoading } = useCurrentFarm();

  const [typeFilter, setTypeFilter] = useState('all');
  const [itemFilter, setItemFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [pageHistory, setPageHistory] = useState<string[]>([]);

  const params = useMemo(() => {
    const p: Record<string, string> = { limit: '50' };
    if (typeFilter !== 'all') p.transaction_type = typeFilter;
    if (itemFilter !== 'all') p.item_id = itemFilter;
    if (dateFrom) p.start_date = dateFrom;
    if (dateTo) p.end_date = dateTo;
    if (cursor) p.cursor = cursor;
    return p;
  }, [typeFilter, itemFilter, dateFrom, dateTo, cursor]);

  const { data, isLoading, error, refetch } = useQuery<ApiListResponse<InventoryTransaction>>({
    queryKey: ['inventory', 'transactions', farmId, params],
    queryFn: () => inventoryApi.listTransactions(farmId!, params),
    staleTime: 15 * 1000,
    enabled: !!farmId,
  });

  const transactions = data?.data ?? [];
  const meta = data?.meta;
  const hasMore = meta?.has_more === true;
  const nextCursor = meta?.cursor ?? undefined;

  function handleNextPage() {
    if (nextCursor) {
      setPageHistory((prev) => [...prev, cursor ?? '']);
      setCursor(nextCursor);
    }
  }

  function handlePrevPage() {
    if (pageHistory.length > 0) {
      const prev = [...pageHistory];
      const prevCursor = prev.pop();
      setPageHistory(prev);
      setCursor(prevCursor || undefined);
    }
  }

  // Fetch items for filter dropdown
  const { data: itemsData } = useQuery({
    queryKey: ['inventory', 'items', farmId, 'filter-list'],
    queryFn: () => inventoryApi.listItems(farmId!, { is_active: 'true', limit: '200' }),
    staleTime: 60 * 1000,
    enabled: !!farmId,
  });
  const itemOptions = [
    { value: 'all', label: 'All Items' },
    ...(itemsData?.data ?? []).map((item: { id: string; name: string }) => ({
      value: item.id,
      label: item.name,
    })),
  ];

  function resetFilters() {
    setTypeFilter('all');
    setItemFilter('all');
    setDateFrom('');
    setDateTo('');
    setCursor(undefined);
    setPageHistory([]);
  }

  if (isFarmLoading || isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <LoadingSpinner size={32} className="text-primary-600" />
        <p className="text-gray-500">Loading transactions...</p>
      </div>
    );
  }

  if (error) {
    return (
      <ErrorMessage
        message={error instanceof Error ? error.message : 'Failed to load transactions'}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-col lg:flex-row items-start lg:items-end gap-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-400 mt-6" />
            <Select
              label="Type"
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setCursor(undefined);
                setPageHistory([]);
              }}
              options={[...TRANSACTION_TYPE_OPTIONS]}
            />
          </div>
          <Select
            label="Item"
            value={itemFilter}
            onChange={(e) => {
              setItemFilter(e.target.value);
              setCursor(undefined);
              setPageHistory([]);
            }}
            options={itemOptions}
          />
          <Input
            label="From"
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setCursor(undefined);
              setPageHistory([]);
            }}
          />
          <Input
            label="To"
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setCursor(undefined);
              setPageHistory([]);
            }}
          />
        </div>
        {(typeFilter !== 'all' || itemFilter !== 'all' || dateFrom || dateTo) && (
          <Button variant="ghost" size="sm" onClick={resetFilters}>
            Clear filters
          </Button>
        )}
      </div>

      {/* Transaction table */}
      {transactions.length === 0 ? (
        <Card>
          <Card.Body>
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <History className="h-12 w-12 text-gray-300 mb-4" />
              <h3 className="font-semibold text-gray-900">No transactions found</h3>
              <p className="text-sm text-gray-500 mt-1">
                {typeFilter !== 'all' || itemFilter !== 'all' || dateFrom || dateTo
                  ? 'Try adjusting your filters.'
                  : 'Transaction history will appear after stock movements.'}
              </p>
            </div>
          </Card.Body>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-gray-500">
                  <th className="px-5 py-3 font-medium">Date</th>
                  <th className="px-5 py-3 font-medium">Item</th>
                  <th className="px-5 py-3 font-medium">Type</th>
                  <th className="px-5 py-3 font-medium text-right">Change</th>
                  <th className="px-5 py-3 font-medium text-right">Balance</th>
                  <th className="px-5 py-3 font-medium text-right">Cost</th>
                  <th className="px-5 py-3 font-medium">Supplier / Notes</th>
                  <th className="px-5 py-3 font-medium">User</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {transactions.map((tx) => (
                  <TransactionRow key={tx.id} transaction={tx} />
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-400">
              Showing {transactions.length} transaction{transactions.length !== 1 ? 's' : ''}
              {pageHistory.length > 0 && ` (page ${pageHistory.length + 1})`}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={pageHistory.length === 0}
                onClick={handlePrevPage}
                leftIcon={<ChevronLeft className="h-4 w-4" />}
              >
                Previous
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={!hasMore}
                onClick={handleNextPage}
                rightIcon={<ChevronRight className="h-4 w-4" />}
              >
                Next
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

// ============================================================================
// Transaction row
// ============================================================================

function TransactionRow({ transaction: tx }: { transaction: InventoryTransaction }) {
  const qty = toNumber(tx.quantity);
  const qtyBefore = toNumber(tx.quantity_before);
  const qtyAfter = toNumber(tx.quantity_after);
  const totalCost = tx.total_cost != null ? toNumber(tx.total_cost) : null;

  const isPositive = tx.transaction_type === 'receipt' || tx.transaction_type === 'return';
  const changeSign = isPositive ? '+' : '-';

  return (
    <tr className="hover:bg-gray-50/50 transition-colors">
      <td className="px-5 py-3 text-gray-600 whitespace-nowrap">
        {format(new Date(tx.created_at), 'MMM d, yyyy')}
        <span className="block text-xs text-gray-400">
          {format(new Date(tx.created_at), 'h:mm a')}
        </span>
      </td>
      <td className="px-5 py-3">
        <span className="font-medium text-gray-900">{tx.item_name ?? 'Unknown'}</span>
      </td>
      <td className="px-5 py-3">
        <TransactionTypeBadge type={tx.transaction_type} />
      </td>
      <td className="px-5 py-3 text-right">
        <span
          className={`font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}
        >
          {changeSign}{qty.toLocaleString()}
        </span>
      </td>
      <td className="px-5 py-3 text-right text-gray-600">
        <span className="text-xs text-gray-400">{qtyBefore.toLocaleString()}</span>
        <span className="mx-1 text-gray-300">&rarr;</span>
        <span className="font-medium text-gray-900">{qtyAfter.toLocaleString()}</span>
      </td>
      <td className="px-5 py-3 text-right text-gray-600">
        {totalCost != null ? `P${totalCost.toLocaleString()}` : '-'}
      </td>
      <td className="px-5 py-3 text-gray-500 max-w-[200px] truncate">
        {tx.supplier && <span className="font-medium">{tx.supplier}</span>}
        {tx.supplier && tx.notes && <span className="mx-1 text-gray-300">|</span>}
        {tx.notes && <span className="text-gray-400">{tx.notes}</span>}
        {!tx.supplier && !tx.notes && '-'}
      </td>
      <td className="px-5 py-3 text-gray-500 text-xs">
        {tx.created_by_name ?? tx.created_by.slice(0, 8)}
      </td>
    </tr>
  );
}

function TransactionTypeBadge({ type }: { type: string }) {
  const config: Record<string, { icon: typeof ArrowDownCircle; variant: 'ok' | 'warning' | 'critical' | 'neutral' | 'success'; label: string }> = {
    receipt: { icon: ArrowDownCircle, variant: 'success', label: 'Receipt' },
    consumption: { icon: Minus, variant: 'warning', label: 'Consumption' },
    adjustment: { icon: RotateCcw, variant: 'neutral', label: 'Adjustment' },
    return: { icon: ArrowUpCircle, variant: 'ok', label: 'Return' },
    disposal: { icon: Trash2, variant: 'critical', label: 'Disposal' },
    transfer: { icon: ArrowLeftRight, variant: 'neutral', label: 'Transfer' },
  };

  const entry = config[type] || { icon: History, variant: 'neutral' as const, label: type };
  const Icon = entry.icon;

  return (
    <Badge variant={entry.variant} size="sm">
      <Icon className="h-3 w-3 mr-0.5" />
      {entry.label}
    </Badge>
  );
}
